/**
 * Module-level tracker for the Document Library background bulk-delete job.
 *
 * The bulk-delete "in progress" guard, the running job id, and the status
 * poller must OUTLIVE the Document Library page component - otherwise
 * navigating away and back loses them: the guard resets (documents become
 * selectable/actionable again mid-delete) and nothing refreshes the list when
 * the job finishes. Keeping this state in a singleton (with the job id mirrored
 * to localStorage so it also survives a full page reload) fixes both:
 *
 *   - Any mounted KBDocumentsPage reads the live snapshot to gate its UI, so
 *     the guard is restored on remount.
 *   - A single poller runs here regardless of what's mounted, fires the
 *     completion toast exactly once (globally), and refreshes whichever page
 *     is currently mounted.
 *
 * Only the >N background path uses this. The small synchronous path completes
 * in a couple of seconds and has no cross-navigation window worth persisting.
 */
import { toast } from 'sonner';

import { documentService } from '@/services/kbService';
import { logger } from '@/utils/logger';

const STORAGE_KEY = 'kb:bulkDeleteJob';
const POLL_MS = 2000;

export interface BulkDeleteSnapshot {
  /** A background delete is in progress - gate document actions on this. */
  active: boolean;
  jobId: string | null;
  /** Document count the job started with (for the loader label). */
  total: number;
}

interface PersistedJob {
  jobId: string;
  total: number;
}

let snapshot: BulkDeleteSnapshot = { active: false, jobId: null, total: 0 };
let polling = false;
// Refresh callback registered by the currently-mounted page (its
// loadDocuments). Null when no page is mounted - the job still finishes; the
// next mount simply loads a fresh list.
let onFinishRefresh: (() => void) | null = null;
const listeners = new Set<(s: BulkDeleteSnapshot) => void>();

function persist(job: PersistedJob | null): void {
  try {
    if (job) localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable - poller still works within this session */
  }
}

function readPersisted(): PersistedJob | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedJob) : null;
  } catch {
    return null;
  }
}

function emit(next: Partial<BulkDeleteSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l(snapshot));
}

export function getBulkDeleteSnapshot(): BulkDeleteSnapshot {
  return snapshot;
}

export function subscribeBulkDelete(l: (s: BulkDeleteSnapshot) => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Register the mounted page's list-refresh callback (cleared on unmount). */
export function setBulkDeleteRefresh(fn: (() => void) | null): void {
  onFinishRefresh = fn;
}

function startPoller(): void {
  if (polling) return; // one poller only - idempotent across mounts/reloads
  const job = readPersisted();
  if (!job) return;
  polling = true;
  emit({ active: true, jobId: job.jobId, total: job.total });

  const tick = async (): Promise<void> => {
    const current = readPersisted();
    if (!current) {
      polling = false;
      emit({ active: false, jobId: null, total: 0 });
      return;
    }
    let finished = false;
    let failed = false;
    let error = '';
    try {
      const st = await documentService.getBulkDeleteStatus(current.jobId);
      if (st.status === 'finished') finished = true;
      else if (st.status === 'failed') {
        failed = true;
        error = (st as any).error || '';
      }
    } catch (e) {
      // Transient status error - keep polling rather than abandon the job.
      logger.error('Bulk delete status poll failed:', e);
    }

    if (finished || failed) {
      persist(null);
      polling = false;
      // Notify listeners FIRST so a mounted page dismisses its page-scoped
      // loader before we raise the global completion toast (no overlap).
      emit({ active: false, jobId: null, total: 0 });
      if (finished) {
        toast.success('Bulk delete completed');
      } else {
        toast.error('Bulk delete failed', { description: error || 'Background job failed' });
      }
      onFinishRefresh?.();
      return;
    }
    setTimeout(tick, POLL_MS);
  };
  tick();
}

/** Begin tracking a newly-enqueued background bulk-delete job. */
export function startBulkDeleteJob(jobId: string, total: number): void {
  persist({ jobId, total });
  emit({ active: true, jobId, total });
  startPoller();
}

/**
 * Resume tracking a job that outlived a full page reload (module state reset
 * but localStorage still holds the job id). No-op on SPA navigation, where the
 * singleton and its poller are already alive.
 */
export function resumeBulkDeleteJob(): void {
  if (readPersisted()) startPoller();
}
